import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../../utils/cn';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive' | 'warning';
  icon?: React.ReactNode;
  className?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  icon,
  className
}) => {
  const getVariantConfig = () => {
    switch (variant) {
      case 'destructive':
        return {
          icon: <AlertTriangle className="h-6 w-6 text-danger" />,
          confirmVariant: 'destructive' as const,
          confirmText: 'Delete',
          className: 'border border-danger/30'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="h-6 w-6 text-warning" />,
          confirmVariant: 'primary' as const,
          confirmText: 'Continue',
          className: 'border border-warning/30'
        };
      default:
        return {
          icon: icon || <AlertTriangle className="h-6 w-6 text-accent" />,
          confirmVariant: 'primary' as const,
          confirmText,
          className: 'border border-border'
        };
    }
  };

  const config = getVariantConfig();

  // Handle keyboard events
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
        onClose();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onConfirm, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'var(--modal-backdrop)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'w-full max-w-md mx-4 card-surface rounded-lg shadow-elev2 bg-[var(--bg-elev)]',
              config.className,
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {config.icon}
                  <h3 className="text-lg font-semibold text-textPrimary">{title}</h3>
                </div>
                <button
                  onClick={onClose}
                  className="text-textMuted hover:text-textPrimary transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <p className="text-textMuted mb-6">{message}</p>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                >
                  {cancelText}
                </Button>
                <Button
                  variant={config.confirmVariant}
                  size="sm"
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                >
                  {config.confirmText}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { ConfirmationModal }; 